import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { getLocale } from 'umi';
import classNames from 'classnames';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import TransferModal from '../TransferModal';
import { ITransferModalRef, TTransferOption } from '../TransferModal/type';
import { ITransferModalSelectorProps } from './type';
import './index.scss';

const TransferModalSelector = (
  props: ITransferModalSelectorProps,
  ref?: React.Ref<ITransferModalRef>,
) => {
  const {
    buttonStyle,
    buttonClassName,
    defaultValue = [],
    defaultVisible = false,
    value,
    primaryName = 'text',
    placeholderCn = '请选择',
    placeholderEn = 'Please choose ',
    isEN,
    buttonTextCn,
    buttonTextEn,
    visible: propsVisible,
    showMaxNameCount = 2,
    rightOptions,
    onConfirm,
    onCancel,
    onTransferModalOpen,
    ...otherParams
  } = props;

  const [visible, setVisible] = useState(defaultVisible);
  const [rightOptionList, setRightOptionList] = useState<TTransferOption[]>([]);

  const [currentOptions, setCurrentOptions] =
    useState<TTransferOption[]>(defaultValue);

  useEffect(() => {
    if (rightOptions) {
      setRightOptionList(rightOptions);
    }
  }, [rightOptions]);

  useEffect(() => {
    if (value) {
      setCurrentOptions(value);
      setRightOptionList(value);
    }
  }, [value]);

  useEffect(() => {
    if (visible) {
      setRightOptionList(currentOptions);
    }
  }, [visible]);

  useEffect(() => {
    if (propsVisible) {
      setVisible(propsVisible);
    }
  }, [propsVisible]);

  const isEn = isEN ?? getLocale() === LANGUAGE_EN;
  const placeholder = getName(isEn, placeholderEn, placeholderCn);
  const buttonText = getName(isEn, buttonTextEn, buttonTextCn);

  const onModalConfirm = (optionList: TTransferOption[]) => {
    onConfirm?.(optionList);
    setVisible(false);
  };

  const onModalCancel = () => {
    onCancel?.();
    setVisible(false);
  };

  const onModalOpen = () => {
    onTransferModalOpen?.();
    setVisible(true);
  };

  // 筛选框展示
  const buttonLabel = useMemo(() => {
    if (currentOptions.length === 0) {
      return placeholder;
    }
    if (currentOptions.length > showMaxNameCount) {
      return currentOptions.length;
    }
    return currentOptions.map((item) => item[primaryName]).toString();
  }, [currentOptions, primaryName, placeholder]);

  return (
    <>
      <div
        style={buttonStyle}
        className={classNames(
          'tea-dropdown dbh-transfer-modal-selector size-m',
          buttonClassName,
        )}
      >
        <div
          className="tea-dropdown__header tea-dropdown-btn"
          onClick={onModalOpen}
        >
          <div className="tea-dropdown__value">
            <div className="dbc-region-select-button-wrap">
              <span className="select-button-text">{buttonText}</span>
              <span
                className={classNames('select-button-value', {
                  'dropdown-value-placeholder': currentOptions.length === 0,
                })}
              >
                {buttonLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
      {visible && (
        <TransferModal
          ref={ref}
          visible={visible}
          isEN={isEn}
          onConfirm={onModalConfirm}
          onCancel={onModalCancel}
          rightOptions={rightOptionList}
          {...otherParams}
        />
      )}
    </>
  );
};

export default forwardRef(TransferModalSelector);
